import type { ModuleMeta } from '../../harness/ModuleRegistry';
import { AtmosphericLayer } from '../_shared/AtmosphericLayer';
import { Wordmark } from '../_nav/Wordmark';

// 03 ПЕРЕЛОМ — DESIGN.md §10 "Capability" slot.
//
// Reconciliation note (2026-05-29 → Igor 2026-06-11):
//   Master prompt §5 had Lora 40px title "Кто сломал правила?" → Igor absorbed
//   that phrasing (plus the "AI changed the economy" thesis) into 02-pain card 3
//   on 2026-05-28 (see WORK_LOG.md "Step 4: 02 PAIN"). To avoid double-stating
//   the same thesis on consecutive screens, the title slot was promoted from the
//   SECOND sentence of master prompt's body — «Окно открылось сейчас.» Igor
//   then approved the breakthrough-timing pivot as «Окно возможностей открылось
//   прямо сейчас» (no trailing period) — the live title. Rubric «ПЕРЕЛОМ» and
//   the two body sentences stay byte-equal to master §5.
//
// Atmosphere config follows §7.8: spheres=2 + shafts=2 + intensity=full →
// cyan-heavy presence (master prompt §5 note: "Атмосферный градиент в этой
// секции уплотняется — больше cyan-присутствия.").
//
// Wave 3 (2026-06-04, WAVE_3_BRIEF.md §4.3):
//   - Mono rubric «ПЕРЕЛОМ» now typewrites letter-by-letter (50ms stagger via
//     .module-03-typewriter--1..7). Leading `//` prefix stays static — reads as
//     the cursor sitting before the word.
//   - Lora display splits into word-spans on `.motion-word-reveal` with
//     `.module-03-word--1..5` (250ms between) — one span per token of «Окно
//     возможностей открылось прямо сейчас».
//   - Body «вайбкодинг» wraps in `.vibe.motion-shimmer` — cyan italic stays as
//     the family rule, gradient shimmer rides on top.

export const meta: ModuleMeta = {
  id: '03-breakthrough',
  title: 'ПЕРЕЛОМ · Capability',
  status: 'approved',
  viewport: '390x844',
};

const RUBRIC_LETTERS = ['П', 'Е', 'Р', 'Е', 'Л', 'О', 'М'];

export default function BreakthroughModule() {
  return (
    <section
      className="relative h-full w-full overflow-hidden bg-surface-main text-text-primary"
      style={{
        paddingTop: 'max(var(--safe-top, 0px), 0.75rem)',
        paddingBottom: 'max(var(--safe-bottom, 0px), 1.5rem)',
      }}
    >
      <AtmosphericLayer spheres={2} shafts={2} intensity="full" />

      <Wordmark variant="nav" />

      <div
        className="relative z-10 flex h-full flex-col px-sp-6"
        style={{ paddingTop: 'calc(max(var(--safe-top, 0px), 1.25rem) + 56px)' }}
      >
        {/* Igor 2026-06-11: «крупный отступ у экрана, нужно поджимать всё близко».
            Was `flex-1 justify-center` → vertically centred the rubric/headline/
            body block, leaving a large empty band above «// ПЕРЕЛОМ». Now
            top-aligned (`flex flex-col`, no flex-1/justify-center) + inner
            paddingTop trimmed 72px → 56px, mirroring 02-pain / 05-mission so the
            rubric sits high and tight. The bottom hint stays pinned via its own
            `mt-auto` on the `h-full` parent. */}
        <div className="flex flex-col gap-sp-7">
          <p className="text-rubric-mono">
            //{' '}
            {RUBRIC_LETTERS.map((letter, i) => (
              <span
                key={i}
                className={`module-03-typewriter module-03-typewriter--${i + 1}`}
              >
                {letter}
              </span>
            ))}
          </p>

          <h1 className="font-display text-display leading-[1.10] tracking-[-0.02em] text-text-primary">
            <span className="motion-word-reveal module-03-word--1">Окно</span>
            <br />
            <span className="motion-word-reveal module-03-word--2">возможностей</span>
            <br />
            <span className="motion-word-reveal module-03-word--3">открылось</span>
            <br />
            <span className="motion-word-reveal module-03-word--4">прямо</span>{' '}
            <span className="motion-word-reveal module-03-word--5">сейчас</span>
          </h1>

          <p className="module-03-rise module-03-rise--3 max-w-[320px] font-ui text-body leading-[1.5] text-text-primary">
            <span className="vibe motion-shimmer">ИИ и вайбкодинг</span> поменяли экономику
            разработки. RAFL использует технологии и знание русского футбола,
            чтобы делать лучшие геймификационные продукты.
          </p>
        </div>

        {/* Universal swipe-down affordance — parity with 01-hero / 02-pain. */}
        <div
          className="hint-arrow-pulse mt-auto flex items-center justify-center gap-sp-3 text-text-muted"
          aria-hidden
        >
          <span className="inline-block h-[14px] w-[14px] rounded-full border border-current" />
          <span className="text-[1.25rem] leading-none">↓</span>
        </div>
      </div>
    </section>
  );
}
